import React, { useCallback, useEffect, useState } from "react";
import { Box, Skeleton, Typography, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Overlay from "components/Overlay/Overlay";
import CardDashboardKPIChart from "components/CardDashboardKPIChart";
import CardDashboardItemTripleData from "components/CardDashboardItemHorizontalMulti";
import DistributionPie from "components/DistributionPie";
import { useStateContext } from "contexts/ContextProvider";
import api from "services/api";

/**
 * Client Numbers — the "numbers" panel for the Clients list (2026-10-30).
 *
 * WHERE THIS CAME FROM
 * The old Dashboard's "Clients" overview rendered these exact figures above a
 * duplicate of the Clients grid. The figures belong next to the list they
 * describe, so they moved here; the duplicate grid did not (the list itself is
 * the grid). Nothing about the numbers changed — same endpoint, same cards,
 * same donuts.
 *
 * `clientTotals` is a company-wide count and ignores the list's search/filters.
 * That is intentional and labelled in the header: these are the figures you
 * quote in a meeting, not the subset currently on screen.
 */
const ClientStatsOverlay = () => {
  const theme = useTheme();
  const { setOverlayName, handleErrorDialog } = useStateContext();

  const [isLoading, setIsLoading] = useState(true);
  const [totals, setTotals] = useState(null);

  const closeOverlay = () => setOverlayName("");

  const fetchTotals = useCallback(() => {
    setIsLoading(true);

    api
      .get("/dashboard/all/clients/clientTotals")
      .then((response) => {
        setTotals(response?.data || null);
        setIsLoading(false);
      })
      .catch((error) => {
        const message =
          error?.response?.data?.message ||
          "Unable to load the client figures. Please try again later.";
        handleErrorDialog(message, theme);
        setIsLoading(false);
      });
  }, [handleErrorDialog, theme]);

  useEffect(() => {
    fetchTotals();
  }, [fetchTotals]);

  const byType = [
    {
      id: 0,
      value: Number(totals?.byType?.Customer) || 0,
      label: "Customer",
      color: theme.palette.primary.main,
    },
    {
      id: 1,
      value: Number(totals?.byType?.Prospect) || 0,
      label: "Prospect",
      color: theme.palette.info[500],
    },
    {
      id: 2,
      value: Number(totals?.byType?.Former) || 0,
      label: "Former",
      color: theme.palette.info[400],
    },
    {
      id: 3,
      value: Number(totals?.byType?.["N/A"]) || 0,
      label: "N/A",
      color: theme.palette.error.main,
    },
  ];

  const byServiceType = [
    {
      id: 0,
      value: Number(totals?.byServiceType?.["OPI/VRI"]) || 0,
      label: "OPI/VRI",
      color: theme.palette.primary.main,
    },
    {
      id: 1,
      value: Number(totals?.byServiceType?.Translation) || 0,
      label: "Translation",
      color: theme.palette.info[500],
    },
    {
      id: 2,
      value: Number(totals?.byServiceType?.["In-Person Interpretation"]) || 0,
      label: "In-Person Interpretation",
      color: theme.palette.info[400],
    },
    {
      id: 3,
      value: Number(totals?.byServiceType?.Training) || 0,
      label: "Training",
      color: theme.palette.warning.main,
    },
    {
      id: 4,
      value: Number(totals?.byServiceType?.Consulting) || 0,
      label: "Consulting",
      color: theme.palette.success.main,
    },
    {
      id: 5,
      value: Number(totals?.byServiceType?.["N/A"]) || 0,
      label: "N/A",
      color: theme.palette.error.main,
    },
  ];

  const byContract = [
    {
      id: 0,
      value: Number(totals?.byContractStatus?.Current) || 0,
      label: "Current",
      color: theme.palette.primary.main,
    },
    {
      id: 1,
      value: Number(totals?.byContractStatus?.["Needs Contract"]) || 0,
      label: "Needs Contract",
      color: theme.palette.error[400],
    },
    {
      id: 2,
      value: Number(totals?.byContractStatus?.["N/A"]) || 0,
      label: "N/A",
      color: theme.palette.grey[400],
    },
  ];

  return (
    <Overlay
      id="client-numbers"
      open={true}
      onClose={closeOverlay}
      title="Client Numbers"
      maxWidthPx={1400}
      // 2026-11-03 (Amine): wrap the content — a fixed-height shell left a
      // large empty band under the figures.
      height="fit-content"
    >
      <Box sx={{ p: { xs: 1, sm: 2 }, pb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, px: 0.5 }}>
          Every client on record — this is the whole directory, not the rows
          currently filtered in the list.
        </Typography>

        <CardDashboardItemTripleData
          titles={["Total Clients", "Total Requestors", "Total Sub-Accounts"]}
          contents={[
            totals?.totalClients,
            totals?.totalRequestors,
            totals?.totalSubAccounts,
          ]}
          isLoading={isLoading}
        />

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, md: 12 / 3 }}>
            <CardDashboardKPIChart
              title="Clients By Type"
              formatType="Number"
              thisMonth={null}
              lastMonth={null}
              percentage={null}
              chart={
                <DistributionPie data={byType} seriesId="Type" />
              }
              isLoading={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 12 / 3 }}>
            <CardDashboardKPIChart
              title="Clients By Service Type"
              formatType="Number"
              thisMonth={null}
              lastMonth={null}
              percentage={null}
              chart={
                <DistributionPie data={byServiceType} seriesId="ServiceType" />
              }
              isLoading={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 12 / 3 }}>
            <CardDashboardKPIChart
              title="Clients By Contract"
              formatType="Number"
              thisMonth={null}
              lastMonth={null}
              percentage={null}
              chart={
                <DistributionPie data={byContract} seriesId="Contract" />
              }
              isLoading={isLoading}
            />
          </Grid>
        </Grid>

        {isLoading ? (
          <Skeleton
            variant="rounded"
            height={40}
            sx={{ mt: 2, borderRadius: "12px" }}
          />
        ) : (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 2, px: 0.5 }}
          >
            Need trends over time, top clients by revenue, or retention? Those
            live in Reports → Clients.
          </Typography>
        )}
      </Box>
    </Overlay>
  );
};

export default ClientStatsOverlay;
